import { timestampDTOSchema } from '@tempo-io/common-dto';
import { z } from 'zod';
import {
  type Finding,
  findingSchema,
  type Recommendation,
  recommendationSchema,
} from './finding-dto';
import { type TrackedCardChange, trackedCardChanges } from './tracked-item-dto';

/**
 * Pin one recommendation for yourself. `loopId`/`runId` name the run the reader was looking at: the
 * server snapshots the card from that run rather than from client-supplied text.
 */
export const pinBodySchema = z.object({
  loopId: z.number().int().positive().nullable(),
  runId: z.string().min(1),
  findingId: z.string().min(1),
  recommendationId: z.string().min(1),
});
export type PinBody = z.output<typeof pinBodySchema>;

/** Without `loopId`, every pin the caller holds in the org (the All loops view). */
export const pinListQuerySchema = z.object({
  loopId: z.coerce.number().int().positive().optional(),
});
export type PinListQuery = z.output<typeof pinListQuerySchema>;

/**
 * The card exactly as it was on the run when it was pinned: the whole finding (headline, statement,
 * evidence, every recommendation) and the pinned recommendation itself. Never rewritten; what later
 * runs did to the card is `latestCard`.
 */
export interface PinSnapshot {
  finding: Finding;
  recommendation: Recommendation;
}

/** A pin is stale once no run has carried its item for this many days. */
export const PIN_STALE_AFTER_DAYS = 14;

// Documents the shape for the OpenAPI spec only.
export const pinSnapshotSchema = z.object({
  finding: findingSchema,
  recommendation: recommendationSchema,
});

// Loose on purpose, like the tracked-item card: a card is served as it was stored, and one written
// under an older finding shape must still parse, or one old pin would fail the whole list. Cast to
// `PinSnapshot` to read it.
const pinCardSchema = z.object({
  finding: z.record(z.string(), z.unknown()),
  recommendation: z.record(z.string(), z.unknown()),
});

export const pinDTOSchema = z.object({
  id: z.string(),
  organizationId: z.string(),
  userId: z.string(),
  loopId: z.number().int().positive().nullable(),
  findingId: z.string(),
  recommendationId: z.string(),
  /** The run the card was pinned from. Soft reference: the run may be pruned; the pin outlives it. */
  runId: z.string(),
  /** Order within the caller's pins for the loop; a new pin goes last. */
  position: z.number().int(),
  /** The card as it was pinned. Never rewritten. */
  snapshot: pinCardSchema,
  /** What to draw: the card as the latest run that carried it showed it, else the snapshot. */
  card: pinCardSchema.nullish(),
  /** The run that holds `card`; actions taken on the card name it. */
  cardRunId: z.string().nullish(),
  /** What the latest completed run did to the card; null before any run since the pin. */
  latestChange: z.enum(trackedCardChanges).nullish(),
  /** Why, in a line: "metric 12% → 0%", the writer's change note, or where the finding went. */
  latestChangeNote: z.string().nullish(),
  latestChangeRunId: z.string().nullish(),
  /**
   * Derived at read time: the latest run did not carry the item, and no run has carried it in the
   * past `PIN_STALE_AFTER_DAYS`. One run carrying it in that window is enough to not be stale. The
   * pin stays until its owner unpins it. Defaults to false so a client parses an older backend.
   */
  stale: z.boolean().default(false),
  /** When a run last carried the item; null on a pin from before this was recorded. */
  lastSeenAt: z.string().nullish(),
  ...timestampDTOSchema.shape,
});
export type PinDTO = Omit<z.infer<typeof pinDTOSchema>, 'snapshot' | 'card' | 'latestChange'> & {
  snapshot: PinSnapshot;
  card?: PinSnapshot | null;
  latestChange?: TrackedCardChange | null;
};
